<template>
  <nav :class="compact ? 'flex flex-col items-center gap-1' : 'space-y-1'">
    <div v-if="lots.length > 0 && !compact" class="w-full">
      <button
        type="button"
        class="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-slate-300 transition-colors"
        @click="toggleMyAreas"
      >
        <div class="flex items-center gap-2">
          <IconMapPin class="w-4 h-4" />
          <span v-translate="'nav.myAreas'"></span>
        </div>
        <IconChevronDown
          :class="['w-4 h-4 transition-transform duration-200', isMyAreasExpanded ? 'rotate-180' : '']"
        />
      </button>

      <div
        ref="myAreasContentRef"
        :style="myAreasContentStyle"
        :class="[
          'overflow-hidden transition-[height,opacity] duration-200 ease-out',
          isMyAreasExpanded ? 'opacity-100 overflow-y-auto' : 'opacity-0 pointer-events-none'
        ]"
      >
        <a
          v-for="lot in lots"
          :key="lot.organizationId"
          :href="`/dashboard/youpark/${lot.organizationId}`"
          class="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white"
        >
          <span>{{ lot.organizationName }}</span>
        </a>
      </div>
    </div>

    <div v-if="showAdministrationMenu" :class="compact ? 'relative' : 'w-full'">
      <button
        v-if="compact"
        type="button"
        :class="[
          'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200',
          showAdminPopup
            ? 'bg-gray-100 dark:bg-slate-800'
            : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
        ]"
        :title="translate('nav.administration')"
        @click="toggleAdminPopup"
      >
        <IconMenu class="w-5 h-5" />
      </button>

      <button
        v-if="!compact"
        type="button"
        class="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors dark:text-slate-400 dark:hover:text-slate-300"
        @click="toggleAdministration"
      >
        <div class="flex items-center gap-2">
          <IconMenu class="w-4 h-4" />
          <span v-translate="'nav.administration'"></span>
        </div>
        <IconChevronDown
          :class="['w-4 h-4 transition-transform duration-200', isAdministrationExpanded ? 'rotate-180' : '']"
        />
      </button>

      <div
        v-if="compact && showAdminPopup"
        class="fixed left-20 z-50 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden transition-colors duration-300 max-h-[80vh] overflow-y-auto"
        :style="{ top: adminPopupTop }"
      >
        <div class="p-2">
          <AdminNavigationMenu
            :permissions="permissions"
            :can-access-you-park-customer="canAccessYouParkCustomer"
            :active-view="activeView"
            @close="closeAdminPopup"
            @navigate-customers="
              () => {
                handleNavigateToYouparkCustomers();
                closeAdminPopup();
              }
            "
            @navigate-customers-remit="
              () => {
                handleNavigateToYouparkCustomersRemit();
                closeAdminPopup();
              }
            "
          />
        </div>
      </div>

      <div
        v-if="!compact"
        ref="adminContentRef"
        :style="adminContentStyle"
        :class="[
          'overflow-hidden transition-[height,opacity] duration-200 ease-out',
          isAdministrationExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'
        ]"
      >
        <AdminNavigationMenu
          :permissions="permissions"
          :can-access-you-park-customer="canAccessYouParkCustomer"
          :active-view="activeView"
          @navigate-customers="handleNavigateToYouparkCustomers"
          @navigate-customers-remit="handleNavigateToYouparkCustomersRemit"
        />
      </div>
    </div>

    <div
      :class="[
        compact ? '' : sectionSpacing,
        isAdministrationExpanded && !compact ? 'border-t border-slate-200 pt-2 dark:border-slate-800' : ''
      ]"
    >
      <button
        v-if="!isRestrictedView('payment')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          isPaymentViewActive
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('payment')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="
          compact ? translate('nav.paymentQuick') : isRestrictedView('payment') ? translate('nav.signIn') : undefined
        "
        @click="handleNavigate('payment')"
      >
        <IconPayWithin48Modern class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.paymentQuick'"></span>
      </button>

      <RouterLink
        :to="{ name: 'map-parking' }"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          activeView === 'map-parking'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="compact ? translate('nav.parkering') : undefined"
        @click.prevent="handleMapNavigate('map-parking')"
      >
        <IconParking class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.parkering'"></span>
      </RouterLink>

      <RouterLink
        :to="{ name: 'map-toll' }"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          activeView === 'map-toll'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="compact ? translate('nav.bomvei') : undefined"
        @click.prevent="handleMapNavigate('map-toll')"
      >
        <IconRoad class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.bomvei'"></span>
      </RouterLink>
    </div>

    <div
      v-if="hasMainNavigationItems"
      :class="[compact ? '' : sectionSpacing, !compact ? 'border-t border-slate-200 pt-2 dark:border-slate-800' : '']"
    >
      <button
        v-if="!isRestrictedView('home')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          activeView === 'home'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('home')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="compact ? translate('nav.home') : isRestrictedView('home') ? translate('nav.signIn') : undefined"
        @click="handleNavigate('home')"
      >
        <IconHome class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.home'"></span>
      </button>
      <button
        v-if="!isRestrictedView('profile')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          activeView === 'profile'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('profile')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="compact ? translate('nav.profile') : isRestrictedView('profile') ? translate('nav.signIn') : undefined"
        @click="handleNavigate('profile')"
      >
        <IconUser class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.profile'"></span>
      </button>
      <button
        v-if="!isRestrictedView('subscriptions')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          isSubscriptionsActive
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('subscriptions')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="
          compact
            ? translate('nav.subscriptions')
            : isRestrictedView('subscriptions')
              ? translate('nav.signIn')
              : undefined
        "
        @click="handleNavigate('subscriptions')"
      >
        <IconSubscription class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.subscriptions'"></span>
      </button>
      <button
        v-if="!isRestrictedView('vehicles')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          isVehiclesActive
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('vehicles')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="
          compact ? translate('nav.vehicles') : isRestrictedView('vehicles') ? translate('nav.signIn') : undefined
        "
        @click="handleNavigate('vehicles')"
      >
        <IconCar class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.vehicles'"></span>
      </button>
      <button
        v-if="!isRestrictedView('payment-sources')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          isPaymentSourcesActive
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('payment-sources')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="
          compact
            ? translate('nav.paymentSource')
            : isRestrictedView('payment-sources')
              ? translate('nav.signIn')
              : undefined
        "
        @click="handleNavigate('payment-sources')"
      >
        <IconCreditCard class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.paymentSource'"></span>
      </button>
      <button
        v-if="!isRestrictedView('bookings')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          activeView === 'bookings'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('bookings')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="
          compact ? translate('nav.bookings') : isRestrictedView('bookings') ? translate('nav.signIn') : undefined
        "
        @click="handleNavigate('bookings')"
      >
        <IconCalendar class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.bookings'"></span>
      </button>
      <button
        v-if="!isRestrictedView('coupons')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          activeView === 'coupons'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('coupons')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="compact ? translate('nav.coupons') : isRestrictedView('coupons') ? translate('nav.signIn') : undefined"
        @click="handleNavigate('coupons')"
      >
        <IconTicket class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.coupons'"></span>
      </button>
      <button
        v-if="!isRestrictedView('receipts')"
        :class="[
          compact
            ? 'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200'
            : 'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 border border-transparent',
          activeView === 'receipts'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : isRestrictedView('receipts')
              ? 'text-gray-500 dark:text-slate-400 border border-dashed border-emerald-400/40 bg-emerald-50/40 dark:bg-slate-800/40 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50'
              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        :title="
          compact ? translate('nav.receipts') : isRestrictedView('receipts') ? translate('nav.signIn') : undefined
        "
        @click="handleNavigate('receipts')"
      >
        <IconReceipt class="w-5 h-5" />
        <span v-if="!compact" v-translate="'nav.receipts'"></span>
      </button>
    </div>

    <div v-if="!compact" :class="[sectionSpacing, 'border-t border-slate-200 pt-2 dark:border-slate-800']">
      <RouterLink
        :to="{ name: 'customer-service' }"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200"
        :class="
          activeView === 'customer-service'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        "
      >
        <IconHelpCircle class="w-5 h-5" />
        <span v-translate="'nav.customerService'"></span>
      </RouterLink>
      <button
        :class="[
          'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
          activeView === 'tour'
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        ]"
        @click="handleNavigate('tour')"
      >
        <IconMap class="w-5 h-5" />
        <span v-translate="'nav.tour'"></span>
      </button>
      <RouterLink
        :to="{ name: 'faq' }"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200"
        :class="
          isFaqActive
            ? 'bg-green-500 text-white font-medium shadow-sm'
            : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
        "
      >
        <IconMessageCircle class="w-5 h-5" />
        <span v-translate="'nav.faq'"></span>
      </RouterLink>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { useAppConfig } from "@core/composables/data/useAppConfig";
import { usePermissions } from "@core/composables/data/usePermissions";
import { useNavigation, type ViewType } from "@core/composables/state/useNavigation";
import { useQuickNav } from "@core/composables/state/useQuickNav";
import IconCalendar from "@core/icons/IconCalendar.vue";
import IconCar from "@core/icons/IconCar.vue";
import IconChevronDown from "@core/icons/IconChevronDown.vue";
import IconCreditCard from "@core/icons/IconCreditCard.vue";
import IconHelpCircle from "@core/icons/IconHelpCircle.vue";
import IconHome from "@core/icons/IconHome.vue";
import IconMap from "@core/icons/IconMap.vue";
import IconMapPin from "@core/icons/IconMapPin.vue";
import IconMenu from "@core/icons/IconMenu.vue";
import IconMessageCircle from "@core/icons/IconMessageCircle.vue";
import IconParking from "@core/icons/IconParking.vue";
import IconPayWithin48Modern from "@core/icons/IconPayWithin48_Modern.vue";
import IconReceipt from "@core/icons/IconReceipt.vue";
import IconRoad from "@core/icons/IconRoad.vue";
import IconSubscription from "@core/icons/IconSubscription.vue";
import IconTicket from "@core/icons/IconTicket.vue";
import IconUser from "@core/icons/IconUser.vue";
import { adminViews, hasAdministrationAccess } from "@core/utils/accessPolicy";

import { useTranslate } from "@common/composables/useTranslate";

import AdminNavigationMenu from "./AdminNavigationMenu.vue";

interface Props {
  sectionSpacing?: string;
  compact?: boolean;
}

withDefaults(defineProps<Props>(), {
  sectionSpacing: "mt-2",
  compact: false
});

const { activeView, navigateTo, showMobileSidebar } = useNavigation();
const { config } = useAppConfig();
const translate = useTranslate();
const { canAccessYouParkCustomer, permissions, lots } = usePermissions();
const { open: openQuickNav } = useQuickNav();
const publicViews = new Set([...(config.publicViews ?? []), "payment"]);

const isRestrictedView = (view: ViewType) => !config.isAuthenticated && !publicViews.has(view);
const isAdministrationExpanded = ref(adminViews.has(activeView.value));
const isMyAreasExpanded = ref(true);
const showAdminPopup = ref(false);
const adminPopupTop = ref("0px");
const adminContentRef = ref<HTMLElement | null>(null);
const myAreasContentRef = ref<HTMLElement | null>(null);
const adminContentHeight = ref(0);
const myAreasContentHeight = ref(0);

const toggleAdministration = () => {
  isAdministrationExpanded.value = !isAdministrationExpanded.value;
};

const toggleMyAreas = () => {
  isMyAreasExpanded.value = !isMyAreasExpanded.value;
};

const toggleAdminPopup = (event: MouseEvent) => {
  const viewportHeight = window.innerHeight;
  const popupMaxHeight = viewportHeight * 0.8;
  const clickPosition = event.clientY || parseFloat(adminPopupTop.value) || viewportHeight * 0.1;
  const spaceBelow = viewportHeight - clickPosition;
  const spaceAbove = clickPosition;

  let top = clickPosition;
  if (spaceBelow < popupMaxHeight && spaceAbove > spaceBelow) {
    top = Math.max(viewportHeight * 0.1, clickPosition - popupMaxHeight);
  } else {
    top = Math.min(clickPosition, viewportHeight - popupMaxHeight - 20);
  }

  adminPopupTop.value = `${top}px`;
  showAdminPopup.value = !showAdminPopup.value;
};

const closeAdminPopup = () => {
  showAdminPopup.value = false;
};

const navigationLocks = ref<Set<ViewType>>(new Set());
const lastNavigation = ref<{ view: ViewType | null; timestamp: number }>({ view: null, timestamp: 0 });
const navigationCooldownMs = 600;

const runNavigationWithLock = async (view: ViewType, action: () => Promise<void>) => {
  const now = Date.now();
  if (navigationLocks.value.has(view)) {
    return;
  }
  if (activeView.value === view) {
    const { view: lastView, timestamp } = lastNavigation.value;
    if (lastView === view && now - timestamp < navigationCooldownMs) {
      return;
    }
  }

  const nextLocks = new Set(navigationLocks.value);
  nextLocks.add(view);
  navigationLocks.value = nextLocks;
  lastNavigation.value = { view, timestamp: now };

  try {
    await action();
  } finally {
    const updatedLocks = new Set(navigationLocks.value);
    updatedLocks.delete(view);
    navigationLocks.value = updatedLocks;
    lastNavigation.value = { view, timestamp: Date.now() };
  }
};

const handleNavigate = (view: ViewType) => {
  void runNavigationWithLock(view, () => navigateTo(view));
};

watch(
  () => activeView.value,
  value => {
    isAdministrationExpanded.value = adminViews.has(value);
    showAdminPopup.value = false;
  }
);

const handleNavigateToYouparkCustomers = () => {
  openQuickNav();
};

const handleNavigateToYouparkCustomersRemit = () => {
  openQuickNav();
};

const handleMapNavigate = (view: "map-parking" | "map-toll") => {
  void navigateTo(view);
};

watch(
  () => showMobileSidebar.value,
  value => {
    if (value && !adminViews.has(activeView.value)) {
      isAdministrationExpanded.value = false;
    }
  }
);

const isFaqActive = computed(() => {
  return activeView.value === "faq" || activeView.value === "faq-item" || activeView.value === "faq-section";
});

const isVehiclesActive = computed(() => {
  return activeView.value === "vehicles" || activeView.value === "add-vehicle" || activeView.value === "edit-vehicle";
});

const isPaymentViewActive = computed(() => {
  return activeView.value === "payment";
});

const isPaymentSourcesActive = computed(() => {
  return (
    activeView.value === "payment-sources" ||
    activeView.value === "add-payment-source" ||
    activeView.value === "edit-payment-source"
  );
});

const isSubscriptionsActive = computed(() => {
  return (
    activeView.value === "subscriptions" ||
    activeView.value === "subscription-details" ||
    activeView.value === "subscription-shared-details" ||
    activeView.value === "subscription-purchase" ||
    activeView.value === "subscription-renew"
  );
});

const showAdministrationMenu = computed(() => hasAdministrationAccess(config));

const hasMainNavigationItems = computed(() => {
  return (
    !isRestrictedView("home") ||
    !isRestrictedView("profile") ||
    !isRestrictedView("vehicles") ||
    !isRestrictedView("payment-sources") ||
    !isRestrictedView("subscriptions") ||
    !isRestrictedView("bookings") ||
    !isRestrictedView("coupons") ||
    !isRestrictedView("receipts")
  );
});

const measureHeight = (element: HTMLElement | null) => (element ? element.scrollHeight : 0);

const updateSectionHeights = () => {
  adminContentHeight.value = measureHeight(adminContentRef.value);
  myAreasContentHeight.value = measureHeight(myAreasContentRef.value);
};

const adminContentStyle = computed(() => ({
  height: isAdministrationExpanded.value ? `${adminContentHeight.value}px` : "0px"
}));

const myAreasContentStyle = computed(() => ({
  height: isMyAreasExpanded.value ? `${myAreasContentHeight.value}px` : "0px"
}));

const handleResize = () => {
  updateSectionHeights();
};

onMounted(() => {
  nextTick(updateSectionHeights);
  window.addEventListener("resize", handleResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

watch(
  permissions,
  () => {
    nextTick(updateSectionHeights);
  },
  { deep: true }
);

watch(
  lots,
  () => {
    nextTick(updateSectionHeights);
  },
  { deep: true }
);

watch(
  () => isAdministrationExpanded.value,
  () => {
    nextTick(updateSectionHeights);
  }
);

watch(
  () => isMyAreasExpanded.value,
  () => {
    nextTick(updateSectionHeights);
  }
);
</script>
